import {useQuery} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import {PlayerProfileResponse} from './types';

// Mirrors `useClubDetail`'s style in `src/Hooks/ClubsApi/useClubsQueries.ts`:
// a plain `useQuery`, GET-on-mount, query key namespaced by the param that
// changes what's fetched.
//
// `username` optional: when provided, hits the public
// `GET /players/<username>/profile/`; when omitted, hits the JWT-required
// `GET /players/me/profile/` (the viewer's own profile). Backend routes are
// mounted at `players/` (no `/api` prefix) — see chess repo's
// `player_profile/urls.py` / `core/urls.py`.
//
// `viewAs` is the owner-only "View as" preview: 'member' (a logged-in visitor)
// or 'anonymous' (a logged-out visitor). The backend ignores it for anyone but
// the owner, so it's safe to always send. Namespaced into the query key so each
// preview mode caches separately; `keepPreviousData` keeps the current content
// (and the exit-preview banner) on screen while the switched mode loads.
export type ProfileViewAs = 'member' | 'anonymous';

export const useGetPlayerProfileApi = (
  username?: string,
  viewAs?: ProfileViewAs,
) =>
  useQuery(
    ['playerProfile', username ?? 'me', viewAs ?? 'self'],
    async (): Promise<PlayerProfileResponse> => {
      const base = username
        ? `/players/${username}/profile/`
        : '/players/me/profile/';
      const url = viewAs ? `${base}?view_as=${viewAs}` : base;
      return (await ChessRestClient.get(url)).data;
    },
    {keepPreviousData: true},
  );

export default useGetPlayerProfileApi;
